/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ApolloQueryResult, FetchResult } from '@apollo/client';
import { ApolloClient, from, HttpLink, InMemoryCache } from '@apollo/client';
import { ApolloError } from '@apollo/client/errors';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import envConfig from '@hulu/env-config';
import type { GraphQLFormattedError } from 'graphql';

import { RoutePath } from '../../constants';
import { User } from '../../utils/user';
import result from './introspection';

export type GQLErrorDetail = {
  code: string;
  issue: string;
  field?: string;
  value?: string;
  location?: string;
  status: string;
};

export type GQLError = {
  details: GQLErrorDetail[];
  message: string;
};

const uri = envConfig?.REACT_APP_AD_MANAGER_API
  ? `${envConfig.REACT_APP_AD_MANAGER_API}/gql`
  : 'http://localhost:4000/gql';

const PUBLIC_OPERATIONS = ['FeatureFlags'];

/**
 * This function is used to handle errors in a readable state to help make error
 * handling more generic so that the app can extract error messages from the server easier.
 * @param graphQLErrors ApolloError The error that came back from an Apollo Client call.
 */
const handleGraphQLErrors = (graphQLErrors: ReadonlyArray<GraphQLFormattedError>): GQLError => {
  const errorResult: GQLError = {
    details: [],
    message: '',
  };

  // loop through all graphql errors
  graphQLErrors.forEach((gqlErr) => {
    const extensions: any = gqlErr.extensions || {};
    // we need to make sure there is something in there so we can get the response
    if (extensions && Object.keys(extensions).length && extensions.response) {
      const status = extensions.response?.status;
      if (status === 401) {
        console.warn('User is not authorized');
        User.logout();
        window.location.href = `${window.location.origin}${RoutePath.ADMIN}`;

        return;
      }

      const details = extensions.response?.body?.error?.details;
      errorResult.message = extensions.response?.body?.error?.message;

      // for ach detail issue we want to capture that for showing errors from service
      if (details) {
        details.forEach((errorDetails: GQLErrorDetail) => errorResult.details.push(errorDetails));
      } else {
        const errorStatus: number | undefined = extensions.response.status || extensions.response.body.status;

        errorResult.details.push({
          code: '',
          issue: '',
          status: errorStatus ? String(errorStatus) : '404',
        });
      }
    }
  });

  return errorResult;
};

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    console.log(`[GraphQl error]: ${JSON.stringify(handleGraphQLErrors(graphQLErrors))}`);
  }

  if (networkError) {
    console.log(`[Network error]: ${networkError}`);
  }
});

const authLink = setContext(async ({ operationName }, prevContext) => {
  // Genie OAuth callback: use freshly exchanged id token (temporary allowlist flow until AMS Genie role exists).
  // Apollo 3.8 calls setContext(setter) as setter(graphqlRequest, operation.getContext()); merged query context is prevContext.
  const { headers } = prevContext;

  if (PUBLIC_OPERATIONS.includes(String(operationName))) {
    return { headers };
  }
  const opContext = prevContext as { genieOauthIdToken?: string | null };
  if (Object.prototype.hasOwnProperty.call(opContext, 'genieOauthIdToken')) {
    const token = opContext.genieOauthIdToken;
    if (typeof token === 'string' && token.length > 0) {
      return {
        headers: {
          ...headers,
          authorization: `Bearer ${token}`,
        },
      };
    }
    return { headers: { ...headers } };
  }

  await User.checkIdToken();
  const { idToken } = User.getUserInfo();

  return {
    headers: {
      ...headers,
      authorization: `Bearer ${idToken}`,
    },
  };
});

const httpLink = new HttpLink({ uri });

export const adminGraphqlClient = new ApolloClient({
  cache: new InMemoryCache({
    possibleTypes: result.possibleTypes,
  }),
  link: from([errorLink, authLink, httpLink]),
  connectToDevTools: false,
});

const { query, mutate } = adminGraphqlClient;

// This is an apply method to be able to catch all errors when doing queries
adminGraphqlClient.query = (...args): Promise<ApolloQueryResult<any>> => {
  return query.apply(adminGraphqlClient, args).catch((error) => {
    throw new Error(JSON.stringify(handleGraphQLErrors(error.graphQLErrors)));
  });
};

// This is an apply method to be able to catch all errors when doing mutations
adminGraphqlClient.mutate = (...args: any): Promise<FetchResult<any, Record<string, any>, Record<string, any>>> => {
  return mutate.apply(adminGraphqlClient, args).catch((error: ApolloError) => {
    throw new Error(JSON.stringify(handleGraphQLErrors(error.graphQLErrors)));
  });
};
